import {
  ArrowBack as ArrowBackIcon,
  BugReport as BugReportIcon,
  Circle as CircleIcon,
  Close as CloseIcon,
  DoneAll as DoneAllIcon,
  Done as DoneIcon,
  OpenInFull as ExpandIcon,
  Minimize as MinimizeIcon,
  Send as SendIcon,
} from '@mui/icons-material'
import {
  Avatar,
  Badge,
  Box,
  Chip,
  Collapse,
  Divider,
  Fade,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from '@mui/material'
import { formatDistanceToNow } from 'date-fns'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useActiveUser } from '../../api/slack/useActiveUser'
import { Conversation, QuickMessage, useQuickChat } from '../../context/QuickChat'
import { useWebsocketContext } from '../../context/Websocket/WebsocketProvider'

const PANEL_WIDTH = 340
const PANEL_HEIGHT = 480
const MINIMIZED_HEIGHT = 48

interface MessageBubbleProps {
  message: QuickMessage
  isOwn: boolean
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ message, isOwn }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isOwn ? 'flex-end' : 'flex-start',
        mb: 1,
      }}
    >
      <Box
        sx={{
          maxWidth: '80%',
          px: 1.5,
          py: 0.75,
          borderRadius: 2,
          bgcolor: isOwn ? 'primary.main' : 'background.paper',
          color: isOwn ? 'primary.contrastText' : 'text.primary',
          border: isOwn ? 'none' : '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
          {message.message}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, mt: 0.25 }}>
          <Typography variant="caption" sx={{ opacity: 0.7, fontSize: '0.65rem' }}>
            {formatDistanceToNow(message.timestamp, { addSuffix: true })}
          </Typography>
          {isOwn &&
            (message.read ? (
              <DoneAllIcon sx={{ fontSize: 12, color: 'info.light' }} />
            ) : message.delivered ? (
              <DoneAllIcon sx={{ fontSize: 12, opacity: 0.5 }} />
            ) : (
              <DoneIcon sx={{ fontSize: 12, opacity: 0.5 }} />
            ))}
        </Box>
      </Box>
    </Box>
  )
}

interface ConversationViewProps {
  conversation: Conversation
  currentUserId: string
  onSend: (message: string) => void
  onTyping: (isTyping: boolean) => void
}

const ConversationView: React.FC<ConversationViewProps> = ({ conversation, currentUserId, onSend, onTyping }) => {
  const [inputValue, setInputValue] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [conversation.messages])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value)

    // Send typing indicator
    onTyping(true)

    // Clear previous timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current)
    }

    // Stop typing after 2 seconds of no input
    typingTimeoutRef.current = setTimeout(() => {
      onTyping(false)
    }, 2000)
  }

  const handleSend = () => {
    if (inputValue.trim()) {
      onSend(inputValue)
      setInputValue('')
      onTyping(false)
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current)
      }
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Messages */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 1.5,
          bgcolor: 'background.default',
        }}
      >
        {conversation.messages.length === 0 ? (
          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 4 }}>
            No messages yet. Say hi!
          </Typography>
        ) : (
          conversation.messages.map(msg => (
            <MessageBubble key={msg.id} message={msg} isOwn={msg.fromUserId === currentUserId} />
          ))
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Input */}
      <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Type a message..."
          value={inputValue}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          autoFocus
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" color="primary" onClick={handleSend} disabled={!inputValue.trim()}>
                  <SendIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 3,
            },
          }}
        />
      </Box>
    </Box>
  )
}

export const QuickChatDrawer: React.FC = () => {
  const {
    conversations,
    isDrawerOpen,
    setDrawerOpen,
    activeConversation,
    setActiveConversation,
    sendMessage,
    markAsRead,
    sendTypingIndicator,
    sendTestMessage,
  } = useQuickChat()
  const { connectedUsers, isDev } = useWebsocketContext()
  const { activeUser } = useActiveUser()
  const [isMinimized, setIsMinimized] = useState(false)

  const currentUserId = activeUser?.user?.id ?? ''

  // Get the active conversation if one is selected, or create a placeholder for new conversations
  const getActiveConversation = (): Conversation | null => {
    if (!activeConversation) return null

    const existingConv = conversations.get(activeConversation)
    if (existingConv) return existingConv

    // Create placeholder conversation for test user
    if (activeConversation === '__test_user__') {
      return {
        odUserId: '__test_user__',
        userName: 'Test Mode',
        userImage: undefined,
        messages: [],
        unreadCount: 0,
        lastMessageTime: 0,
        isTyping: false,
      }
    }

    // Create placeholder for a new conversation with a connected user
    const user = connectedUsers.find(u => u.id === activeConversation)
    if (user) {
      return {
        odUserId: activeConversation,
        userName: user.name || `${user.firstName} ${user.lastName}`,
        userImage: user.image_512,
        messages: [],
        unreadCount: 0,
        lastMessageTime: 0,
        isTyping: false,
      }
    }

    return null
  }

  const activeConv = getActiveConversation()

  // Mark messages as read when viewing a conversation
  useEffect(() => {
    if (activeConversation && isDrawerOpen) {
      markAsRead(activeConversation)
    }
  }, [activeConversation, isDrawerOpen, markAsRead])

  const handleSelectUser = (userId: string) => {
    // Create or select conversation
    const existingConv = conversations.get(userId)
    if (!existingConv) {
      const user = connectedUsers.find(u => u.id === userId)
      if (user) {
        // Conversation will be created when first message is sent
      }
    }
    setActiveConversation(userId)
  }

  const handleSendMessage = useCallback(
    (message: string) => {
      if (activeConversation) {
        // Handle test mode differently
        if (activeConversation === '__test_user__') {
          sendTestMessage(message)
        } else {
          sendMessage(activeConversation, message)
        }
      }
    },
    [activeConversation, sendMessage, sendTestMessage]
  )

  const handleTypingIndicator = useCallback(
    (isTyping: boolean) => {
      if (activeConversation) {
        sendTypingIndicator(activeConversation, isTyping)
      }
    },
    [activeConversation, sendTypingIndicator]
  )

  // Sort conversations by last message time
  const sortedConversations = Array.from(conversations.values()).sort((a, b) => b.lastMessageTime - a.lastMessageTime)

  // Create a Set of online user IDs for quick lookup
  const onlineUserIds = new Set(connectedUsers.map(u => u.id))

  // Helper to check if a user is online
  const isUserOnline = (userId: string): boolean => {
    if (userId === '__test_user__') return true // Test user is always "online"
    return onlineUserIds.has(userId)
  }

  // Filter to show only connected users or users with existing conversations
  // In dev mode, include user's own other tabs for testing
  const conversationUserIds = new Set(conversations.keys())
  const availableUsers = connectedUsers.filter(u => {
    // Skip users we already have conversations with
    if (conversationUserIds.has(u.id)) return false
    // Skip our own current tab
    if (u.isOwnTab) return false
    // In dev mode, include our other tabs for testing
    if (isDev && u.isOwnOtherTab) return true
    // Include other users (not ourselves)
    return u.id !== currentUserId
  })

  if (!isDrawerOpen) return null

  return (
    <Fade in={isDrawerOpen}>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          width: PANEL_WIDTH,
          height: isMinimized ? MINIMIZED_HEIGHT : PANEL_HEIGHT,
          zIndex: 1300,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: 2,
          transition: 'height 0.2s ease-in-out',
        }}
      >
        {/* Minimized Header Bar - Always visible */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1.5,
            py: 1,
            bgcolor: 'background.defaultdim',
            color: 'text.primary',
            cursor: 'pointer',
            minHeight: MINIMIZED_HEIGHT,
          }}
          onClick={() => isMinimized && setIsMinimized(false)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {activeConv && !isMinimized && (
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation()
                  setActiveConversation(null)
                }}
                sx={{ color: 'inherit', p: 0.5 }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {activeConv ? activeConv.userName : 'Quick Chat'}
              </Typography>
              {activeConv && !isMinimized && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CircleIcon
                    sx={{
                      fontSize: 8,
                      color: isUserOnline(activeConv.odUserId) ? 'success.main' : 'grey.500',
                    }}
                  />
                  <Typography variant="caption" sx={{ opacity: 0.7, fontSize: '0.65rem' }}>
                    {isUserOnline(activeConv.odUserId) ? 'online' : 'offline'}
                  </Typography>
                </Box>
              )}
            </Box>
            {activeConv?.isTyping && !isMinimized && (
              <Typography variant="caption" sx={{ opacity: 0.8, fontStyle: 'italic' }}>
                typing...
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={e => {
                e.stopPropagation()
                setIsMinimized(!isMinimized)
              }}
              sx={{ color: 'inherit', p: 0.5 }}
            >
              {isMinimized ? <ExpandIcon fontSize="small" /> : <MinimizeIcon fontSize="small" />}
            </IconButton>
            <IconButton
              size="small"
              onClick={e => {
                e.stopPropagation()
                setDrawerOpen(false)
                setActiveConversation(null)
                setIsMinimized(false)
              }}
              sx={{ color: 'inherit', p: 0.5 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Content - Hidden when minimized */}
        <Collapse in={!isMinimized} timeout={200}>
          <Box sx={{ height: PANEL_HEIGHT - MINIMIZED_HEIGHT, display: 'flex', flexDirection: 'column' }}>
            {activeConv ? (
              <ConversationView
                conversation={activeConv}
                currentUserId={currentUserId}
                onSend={handleSendMessage}
                onTyping={handleTypingIndicator}
              />
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                {/* Content */}
                <Box sx={{ flex: 1, overflowY: 'auto' }}>
                  {/* Recent Conversations */}
                  {sortedConversations.length > 0 && (
                    <>
                      <Typography
                        variant="overline"
                        sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'block', color: 'text.secondary', fontSize: '0.65rem' }}
                      >
                        Recent
                      </Typography>
                      <List dense disablePadding>
                        {sortedConversations.map(conv => {
                          const online = isUserOnline(conv.odUserId)
                          return (
                            <ListItem
                              key={conv.odUserId}
                              button
                              onClick={() => handleSelectUser(conv.odUserId)}
                              sx={{
                                py: 0.75,
                                '&:hover': { bgcolor: 'action.hover' },
                              }}
                            >
                              <ListItemAvatar sx={{ minWidth: 44 }}>
                                <Badge
                                  badgeContent={conv.unreadCount}
                                  color="error"
                                  overlap="circular"
                                  anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                                >
                                  <Badge
                                    overlap="circular"
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                    badgeContent={
                                      <CircleIcon
                                        sx={{
                                          fontSize: 10,
                                          color: online ? 'success.main' : 'grey.500',
                                        }}
                                      />
                                    }
                                  >
                                    <Avatar src={conv.userImage} sx={{ width: 32, height: 32 }}>
                                      {conv.userName?.charAt(0)}
                                    </Avatar>
                                  </Badge>
                                </Badge>
                              </ListItemAvatar>
                              <ListItemText
                                primary={conv.userName}
                                secondary={
                                  conv.messages.length > 0
                                    ? conv.messages[conv.messages.length - 1].message
                                    : 'No messages'
                                }
                                primaryTypographyProps={{
                                  variant: 'body2',
                                  fontWeight: conv.unreadCount > 0 ? 600 : 400,
                                  fontSize: '0.85rem',
                                }}
                                secondaryTypographyProps={{
                                  variant: 'caption',
                                  noWrap: true,
                                  sx: { maxWidth: 160 },
                                }}
                              />
                              {conv.isTyping ? (
                                <Chip label="typing..." size="small" sx={{ fontSize: '0.6rem', height: 18 }} />
                              ) : (
                                !online && (
                                  <Chip
                                    label="offline"
                                    size="small"
                                    sx={{ fontSize: '0.6rem', height: 18, bgcolor: 'grey.700', color: 'grey.300' }}
                                  />
                                )
                              )}
                            </ListItem>
                          )
                        })}
                      </List>
                    </>
                  )}

                  {/* Online Users */}
                  {availableUsers.length > 0 && (
                    <>
                      <Divider sx={{ my: 0.5 }} />
                      <Typography
                        variant="overline"
                        sx={{ px: 2, pt: 0.5, pb: 0.5, display: 'block', color: 'text.secondary', fontSize: '0.65rem' }}
                      >
                        {isDev && availableUsers.some(u => u.isOwnOtherTab) ? 'Online Now (Dev Mode)' : 'Online Now'}
                      </Typography>
                      <List dense disablePadding>
                        {availableUsers.map(user => (
                          <ListItem
                            key={`${user.id}-${user.tabId || 'default'}`}
                            button
                            onClick={() => handleSelectUser(user.id)}
                            sx={{
                              py: 0.75,
                              '&:hover': { bgcolor: 'action.hover' },
                            }}
                          >
                            <ListItemAvatar sx={{ minWidth: 44 }}>
                              <Badge
                                overlap="circular"
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                badgeContent={
                                  <CircleIcon
                                    sx={{ fontSize: 8, color: user.isOwnOtherTab ? 'warning.main' : 'success.main' }}
                                  />
                                }
                              >
                                <Avatar
                                  src={user.image_512}
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    bgcolor: user.isOwnOtherTab ? 'warning.light' : undefined,
                                  }}
                                >
                                  {user.firstName?.charAt(0) || user.name?.charAt(0)}
                                </Avatar>
                              </Badge>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                user.isOwnOtherTab
                                  ? `${user.name || user.firstName} (Other Tab)`
                                  : user.name || `${user.firstName} ${user.lastName}`
                              }
                              secondary={
                                user.isOwnOtherTab
                                  ? 'Your other browser tab - for testing'
                                  : `Connected ${formatDistanceToNow(user.connectedAt, { addSuffix: true })}`
                              }
                              primaryTypographyProps={{ variant: 'body2', fontSize: '0.85rem' }}
                              secondaryTypographyProps={{ variant: 'caption' }}
                            />
                            {user.isOwnOtherTab && (
                              <Chip label="DEV" size="small" color="warning" sx={{ fontSize: '0.6rem', height: 18 }} />
                            )}
                          </ListItem>
                        ))}
                      </List>
                    </>
                  )}

                  {/* Empty State / Test Mode (dev only) */}
                  {availableUsers.length === 0 && isDev && (
                    <>
                      <Divider sx={{ my: 0.5 }} />
                      <Typography
                        variant="overline"
                        sx={{ px: 2, pt: 0.5, pb: 0.5, display: 'block', color: 'text.secondary', fontSize: '0.65rem' }}
                      >
                        Test Mode
                      </Typography>
                      <List dense disablePadding>
                        <ListItem
                          button
                          onClick={() => handleSelectUser('__test_user__')}
                          sx={{
                            py: 0.75,
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                        >
                          <ListItemAvatar sx={{ minWidth: 44 }}>
                            <Badge
                              overlap="circular"
                              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                              badgeContent={<BugReportIcon sx={{ fontSize: 10, color: 'warning.main' }} />}
                            >
                              <Avatar sx={{ width: 32, height: 32, bgcolor: 'warning.main' }}>T</Avatar>
                            </Badge>
                          </ListItemAvatar>
                          <ListItemText
                            primary="Test Chat"
                            secondary="Send chats to yourself to test"
                            primaryTypographyProps={{ variant: 'body2', fontSize: '0.85rem' }}
                            secondaryTypographyProps={{ variant: 'caption' }}
                          />
                        </ListItem>
                      </List>
                      {sortedConversations.length === 0 && (
                        <Box sx={{ px: 2, pb: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            No one else is online. Use test mode to try out chat!
                          </Typography>
                        </Box>
                      )}
                    </>
                  )}

                  {/* Empty State (production) */}
                  {availableUsers.length === 0 && !isDev && sortedConversations.length === 0 && (
                    <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        No one else is online right now.
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Footer */}
                <Box
                  sx={{
                    p: 1,
                    borderTop: 1,
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    textAlign="center"
                    sx={{ fontSize: '0.65rem' }}
                  >
                    Messages disappear at midnight
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Collapse>
      </Paper>
    </Fade>
  )
}

export default QuickChatDrawer
